import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Warning, CheckCircle, Prohibit } from "@phosphor-icons/react";

const overlay = {
  position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1100,
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "48px 20px", overflowY: "auto",
};
const box = (bg, fg, bd) => ({
  background: bg, color: fg, border: `1px solid ${bd}`, borderRadius: 6,
  padding: "10px 12px", fontSize: 12.5, marginBottom: 12,
});

function Field({ label, value, testid }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--omh-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div className="text-mono" style={{ fontSize: 13, marginTop: 3 }} data-testid={testid}>{value ?? "—"}</div>
    </div>
  );
}

export function PushToErpDialog({ poId, poNumber, overwriteOlder = false, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [override, setOverride] = useState("");   // admin-corrected ERP Pcode
  const [editing, setEditing] = useState(false);
  const [pq, setPq] = useState("");               // party search term
  const [parties, setParties] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const url = `/po/${poId}/push-to-erp?confirm=false${override ? `&pcode_override=${encodeURIComponent(override)}` : ""}${overwriteOlder ? "&overwrite_older=true" : ""}`;
    api.post(url)
      .then((r) => { if (alive) { setPreview(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || "Preview failed"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [poId, override]);

  useEffect(() => {
    if (!editing || pq.trim().length < 2) { setParties([]); return; }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/portal/parties?q=${encodeURIComponent(pq.trim())}`)
        .then((r) => { if (alive) setParties(r.data || []); })
        .catch(() => { if (alive) setParties([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [pq, editing]);

  const pickParty = (p) => {
    setOverride(p.pcode);
    setEditing(false);
    setPq("");
    setParties([]);
  };
  const clearOverride = () => { setOverride(""); setEditing(false); setPq(""); setParties([]); };

  const doPush = async () => {
    setPushing(true);
    setError(null);
    try {
      const url = `/po/${poId}/push-to-erp?confirm=true${override ? `&pcode_override=${encodeURIComponent(override)}` : ""}${overwriteOlder ? "&overwrite_older=true" : ""}`;
      const r = await api.post(url);
      if (r.data.ok && r.data.pono) {
        setResult(r.data);
        toast.success(`Pushed to ERP as PoNo ${r.data.pono}`);
        onDone && onDone(r.data);
      } else {
        setError((r.data.blockers || []).join(" ") || "Push was blocked by the ERP.");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Push failed");
    } finally { setPushing(false); }
  };

  const blockers = preview?.blockers || [];
  const warnings = preview?.warnings || [];
  const isOverwrite = (preview?.mode || "").includes("overwrite");
  const canPush = preview && preview.ok && blockers.length === 0 && !result;

  return (
    <div className="omh-modal-overlay" style={overlay} onClick={onClose} data-testid="push-erp-dialog">
      <div className="jde-panel" style={{ width: "min(680px, 100%)", background: "#fff", maxHeight: "88vh", overflowY: "auto" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="jde-panel-header" style={{ position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
          <div className="jde-panel-title">Push to MS SQL ERP — {poNumber}</div>
          <button className="jde-btn jde-btn-sm" onClick={onClose} data-testid="push-erp-close"><X size={11} weight="bold" /></button>
        </div>

        <div className="jde-panel-body">
          {loading && <div className="muted" style={{ padding: 14, fontSize: 12.5 }}>Checking ERP &amp; validating fields…</div>}

          {error && (
            <div style={box("#FEF2F2", "#991B1B", "#FCA5A5")} data-testid="push-erp-error">
              <Warning size={13} weight="bold" style={{ display: "inline", marginRight: 4 }} /> {error}
            </div>
          )}

          {result && (
            <div style={box("#DCFCE7", "#166534", "#86EFAC")} data-testid="push-erp-success">
              <CheckCircle size={16} weight="fill" style={{ display: "inline", marginRight: 6, verticalAlign: "-3px" }} />
              Written to the ERP as <strong>PoNo {result.pono}</strong> ({result.poi_written} line{result.poi_written === 1 ? "" : "s"}).
              It will re-sync back into the app on the next MS&nbsp;SQL sync.
              {(result.overwrite_older_result?.retired || []).length > 0 && (
                <div style={{ marginTop: 6 }} data-testid="push-erp-retired-note">
                  Superseded {result.overwrite_older_result.retired.length} older copy(ies): {result.overwrite_older_result.retired.map((r) => `${r.source} #${r.ms_pono ?? "—"}`).join(", ")}.
                </div>
              )}
            </div>
          )}

          {preview && !result && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                <Field label="Customer" value={preview.customer_name} testid="push-erp-customer" />
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--omh-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      ERP Pcode {preview.pcode_overridden && <span style={{ color: "var(--omh-primary)" }}>· edited</span>}
                    </div>
                    {!editing && (
                      <button className="jde-btn jde-btn-sm" onClick={() => setEditing(true)} data-testid="push-erp-edit-pcode"
                              style={{ padding: "1px 8px", fontSize: 11 }}>
                        {preview.pcode_overridden ? "Change" : "Edit"}
                      </button>
                    )}
                  </div>
                  <div className="text-mono" style={{ fontSize: 13, marginTop: 3 }} data-testid="push-erp-pcode">
                    {preview.resolved_pcode || "—"}
                  </div>
                  {preview.resolved_party_name && (
                    <div style={{ fontSize: 11.5, color: "var(--omh-text-muted)", marginTop: 2 }} data-testid="push-erp-party-name">
                      {preview.resolved_party_name}
                    </div>
                  )}
                  {preview.pcode_overridden && !editing && (
                    <button onClick={clearOverride} data-testid="push-erp-reset-pcode"
                            style={{ marginTop: 4, fontSize: 11, color: "var(--omh-primary)", background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                      Reset to auto-resolved
                    </button>
                  )}
                  {editing && (
                    <div style={{ marginTop: 6 }}>
                      <input autoFocus value={pq} onChange={(e) => setPq(e.target.value)}
                             placeholder="Search customer code or name…" data-testid="push-erp-pcode-search"
                             style={{ width: "100%", padding: "6px 8px", fontSize: 12, border: "1px solid var(--omh-divider)", borderRadius: 5 }} />
                      {searching && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Searching…</div>}
                      {parties.length > 0 && (
                        <div style={{ marginTop: 4, maxHeight: 180, overflowY: "auto", border: "1px solid var(--omh-divider)", borderRadius: 5 }}
                             data-testid="push-erp-party-results">
                          {parties.map((p) => (
                            <div key={p.pcode} onClick={() => pickParty(p)} data-testid={`push-erp-party-${p.pcode}`}
                                 style={{ padding: "6px 8px", fontSize: 12, cursor: "pointer", borderBottom: "1px solid var(--omh-divider)" }}
                                 onMouseDown={(e) => e.preventDefault()}>
                              <span className="text-mono" style={{ fontWeight: 700 }}>{p.pcode}</span> — {p.name}
                            </div>
                          ))}
                        </div>
                      )}
                      <button onClick={() => { setEditing(false); setPq(""); setParties([]); }}
                              style={{ marginTop: 4, fontSize: 11, color: "var(--omh-text-muted)", background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <Field label="CustPO" value={preview.resolved_custpo} testid="push-erp-custpo" />
                <Field label="Lines" value={preview.line_count} testid="push-erp-linecount" />
                <Field label="Action"
                       value={isOverwrite ? `Overwrite existing PoNo ${preview.existing_pono}` : "Insert as NEW PO (fresh PoNo)"}
                       testid="push-erp-mode" />
              </div>

              {overwriteOlder && (preview.older_versions_to_retire || []).length > 0 && (
                <div style={box("#FFF7ED", "#9A3412", "#FDBA74")} data-testid="push-erp-overwrite-older">
                  <strong><Warning size={13} weight="bold" style={{ display: "inline", marginRight: 4 }} /> Older version{(preview.older_versions_to_retire.length > 1) ? "s" : ""} will be superseded (backup kept):</strong>
                  <ul style={{ margin: "6px 0 0 18px" }}>
                    {preview.older_versions_to_retire.map((o, i) => (
                      <li key={i}>{o.source} · PO {o.ms_custpo} · #{o.ms_pono ?? "—"} · {o.po_date || "no date"} · open {o.qty_open}</li>
                    ))}
                  </ul>
                </div>
              )}

              {blockers.length > 0 && (
                <div style={box("#FEF2F2", "#991B1B", "#FCA5A5")} data-testid="push-erp-blockers">
                  <strong><Prohibit size={13} weight="bold" style={{ display: "inline", marginRight: 4 }} /> Cannot push — fix these first:</strong>
                  <ul style={{ margin: "6px 0 0 18px" }}>{blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </div>
              )}

              {warnings.length > 0 && (
                <div style={box("#FEF3C7", "#92400E", "#FDE68A")} data-testid="push-erp-warnings">
                  <strong><Warning size={13} weight="bold" style={{ display: "inline", marginRight: 4 }} /> Warnings (you can still push):</strong>
                  <ul style={{ margin: "6px 0 0 18px" }}>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}

              {blockers.length === 0 && warnings.length === 0 && (
                <div style={box("#EFF6FF", "#1E40AF", "#BFDBFE")}>
                  All key fields (Pcode, CustPO, item codes, dates) validated — ready to write to <code>dbo.po</code> / <code>dbo.poi</code>.
                </div>
              )}
            </>
          )}
        </div>

        <div className="jde-panel-body" style={{ position: "sticky", bottom: 0, background: "#fff", borderTop: "1px solid var(--omh-divider)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button className="jde-btn" onClick={onClose} data-testid="push-erp-cancel">{result ? "Close" : "Cancel"}</button>
          {canPush && (
            <button className="jde-btn jde-btn-primary" onClick={doPush} disabled={pushing} data-testid="push-erp-confirm">
              {pushing ? "Pushing…" : "Confirm & Write to ERP"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
